import React, {useRef} from 'react'
import {Box, Image} from '@chakra-ui/react'
import themeBreakpoints from '../../../theme/foundations/breakpoints'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useScrollToLoadVideo} from '_customHooks'

interface VideoProps {
    alt?: string
    videoxl?: string
    videolg?: string
    videomd?: string
    videosm?: string
    videoxs?: string
    position?: 'absolute' | 'relative' | 'fixed' | 'static' | 'sticky'
    zIndex?: number
    objectFit?: 'cover' | 'contain'
    width?: string | {base?: string; sm?: string; md?: string; lg?: string}
    height?: string
    overflow?: string
    autoplay?: boolean
    muted?: boolean
    poster?: string
    loop?: boolean
    controls?: boolean
    isTileImage?: boolean
    isVideoModule?: boolean
    playVideofunc?: () => void
    onTimeUpdateFunc?: (event: React.SyntheticEvent<HTMLVideoElement>) => void
}

/**
 * @name Video
 *
 * @component
 *
 * @description
 * Renders a video with responsive sources and various styling options.
 *
 * @param {VideoProps} props - Properties object.
 * @param {string} props.alt - The alt text for the video.
 * @param {string} props.videoxs - The source URL for the extra small video.
 * @param {string} props.videosm - The source URL for the small video.
 * @param {string} props.videomd - The source URL for the medium video.
 * @param {string} props.videolg - The source URL for the large video.
 * @param {string} props.videoxl - The source URL for the extra large video.
 * @param {string} [props.overflow='visible'] - The overflow property of the video container.
 * @param {string} [props.width='100%'] - The width of the video container.
 * @param {string} [props.height='100%'] - The height of the video container.
 * @param {string} [props.autoplay=false] - The possibility to have a video play when loaded.
 * @param {string} [props.muted=false] - The possibility of having the video muted.
 * @param {string} props.poster - The source URL for an image while video loading.
 * @param {string} [props.controls=false] - The possibility of having the controls shown.
 * @param {string} [props.loop=false] - The possibility of having the video going in loop.
 * @param {string} [props.isTileImage=false] - Adds the styles to the video to be used as a tile image.
 * @param {string} [props.isVideoModule=false] - Adds the styles to the video to be used as a module video, allowing to have an initial space when loading
 *
 * @returns {JSX.Element} The rendered `Picture` component.
 */
export const Video: React.FC<VideoProps> = ({
    alt,
    videoxs,
    videosm,
    videomd,
    videolg,
    videoxl,
    poster,
    loop = false,
    autoplay = false,
    muted = false,
    controls = false,
    isTileImage = false,
    overflow = 'visible',
    width = '100%',
    height = '100%',
    isVideoModule = false,
    playVideofunc,
    onTimeUpdateFunc,
    ...props
}) => {
    const {breakpoints} = themeBreakpoints
    const targetVideo = useRef(null)
    const isReady = useScrollToLoadVideo(targetVideo)
    const {locale} = useMultiSite()
    const lang = locale.id.split('-')[0] || 'en'
    const finalSource = videoxs || videosm || videomd || videolg || videoxl

    return (
        <Box
            ref={targetVideo}
            overflow={overflow}
            top={0}
            width={width}
            height={height}
            pt={isTileImage ? '127.3%' : ''}
            {...props}
            position={isVideoModule ? 'static' : 'relative'}
            data-testid="video"
        >
            {isTileImage && (
                <Image
                    src={getAssetUrl('static/img/product_placeholder.jpg')}
                    alt={`${alt} - Placeholder`}
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                    zIndex={-1}
                />
            )}
            <video
                autoPlay={isReady ? autoplay : false}
                playsInline={isReady}
                muted={autoplay || muted}
                controls={controls}
                loop={loop}
                preload="metadata"
                style={{
                    width: '100%',
                    height: props.position === 'absolute' || props.objectFit === 'cover' ? '100%' : 'auto',
                    position: props.position ? props.position : 'unset',
                    top: props.position === 'absolute' ? '0' : 'unset',
                    left: props.position === 'absolute' ? '0' : 'unset',
                    right: props.position === 'absolute' ? '0' : 'unset',
                    bottom: props.position === 'absolute' ? '0' : 'unset',
                    zIndex: props.zIndex ? props.zIndex : '-1',
                    objectFit: props.objectFit ? props.objectFit : 'unset',
                }}
                poster={poster}
                src={isReady ? finalSource : ''}
                disablePictureInPicture
                onPlay={playVideofunc}
                onTimeUpdate={onTimeUpdateFunc}
            >
                {isReady && videoxl && <source media={`(min-width: ${breakpoints.xl})`} srcSet={videoxl} />}
                {isReady && videolg && <source media={`(min-width: ${breakpoints.lg})`} srcSet={videolg || videoxl} />}
                {isReady && videomd && <source media={`(min-width: ${breakpoints.md})`} srcSet={videomd || videolg || videoxl} />}
                {isReady && videosm && <source media={`(min-width: ${breakpoints.sm})`} srcSet={videosm || videomd || videolg || videoxl} />}
                {isReady && <source srcSet={finalSource} />}
                <track kind="captions" src={finalSource} srcLang={lang} />
                {alt && alt}
            </video>
        </Box>
    )
}
